import { useState, useEffect, useMemo, useRef } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Search, X } from 'lucide-react'
import { clsx } from 'clsx'
import { AppHeader } from '@/shared/components/layout/AppHeader'
import { LeagueSummaryCard } from '@/features/league/components/LeagueSummaryCard'
import { LeagueCardSkeleton } from '@/features/league/components/LeagueCardSkeleton'
import { useUserLeagues, useToggleFavorite } from '@/features/league/hooks/useUserLeagues'
import { fetchUserLeagues, leagueKeys } from '@/features/league/queries'
import { matchesSearch } from '@/shared/lib/normalizeAccents'
import { debounce } from '@/shared/lib/debounce'
import styles from './ligas.module.css'
import { withLegacySkin } from '@/shared/components/layout/LegacySkin'

type LigasSearch = {
  q?:             string
  windowState?:   'open' | 'closed'
  competitionId?: string
}

export const Route = createFileRoute('/_authenticated/ligas')({
  validateSearch: (search: Record<string, unknown>): LigasSearch => {
    const result: LigasSearch = {}
    if (typeof search.q === 'string' && search.q.trim()) {
      result.q = search.q.trim()
    }
    if (search.windowState === 'open' || search.windowState === 'closed') {
      result.windowState = search.windowState
    }
    if (typeof search.competitionId === 'string' && search.competitionId) {
      result.competitionId = search.competitionId
    }
    return result
  },
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData({
      queryKey: leagueKeys.list(),
      queryFn:  fetchUserLeagues,
    }),
  pendingComponent: withLegacySkin(LigasSkeleton),
  pendingMs: 300,
  component: withLegacySkin(LigasPage),
})

function LigasSkeleton() {
  return (
    <>
      <AppHeader title="Suas Ligas" />
      <div className={styles.skeletons}>
        <LeagueCardSkeleton />
        <LeagueCardSkeleton />
        <LeagueCardSkeleton />
      </div>
    </>
  )
}

// One shared interval for all cards — avoids N timers for N leagues
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])
  return now
}

// Build a search object, omitting keys whose values are falsy.
// Accepts optional | undefined for each field so callers may pass derived values
// that could be undefined without a type error under exactOptionalPropertyTypes.
function buildSearch(params: {
  q?:             string | undefined
  windowState?:   'open' | 'closed' | undefined
  competitionId?: string | undefined
}): LigasSearch {
  const result: LigasSearch = {}
  if (params.q)             result.q             = params.q
  if (params.windowState)   result.windowState   = params.windowState
  if (params.competitionId) result.competitionId = params.competitionId
  return result
}

function LigasPage() {
  const { data: leagues = [], isError, refetch } = useUserLeagues()
  const navigate = useNavigate({ from: Route.fullPath })
  const { q, windowState, competitionId } = Route.useSearch()
  const now = useNow()
  const [expandedLeagueIds, setExpandedLeagueIds] = useState<Set<string>>(() => new Set())
  const toggleFavorite = useToggleFavorite()

  // Mobile: toggle search input visibility
  const [searchVisible, setSearchVisible] = useState(() => (q?.length ?? 0) > 0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Keep latest filter state in a ref so the debounced fn always reads current values
  const filtersRef = useRef({ windowState, competitionId })
  filtersRef.current = { windowState, competitionId }

  // Derive unique competitions from league data for filter chips
  const competitions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const l of leagues) {
      if (!seen.has(l.competitionId)) {
        seen.set(l.competitionId, l.competitionName)
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }))
  }, [leagues])

  // Debounced navigate — no useEffect needed; reads latest filters via ref
  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        void navigate({
          search: buildSearch({
            q:             value.trim() || undefined,
            windowState:   filtersRef.current.windowState,
            competitionId: filtersRef.current.competitionId,
          }),
        })
      }, 300),
    [navigate],
  )

  const searchQ = q ?? ''

  const filtered = useMemo(
    () =>
      leagues.filter((league) => {
        if (searchQ && !matchesSearch(league.name, searchQ)) return false
        if (windowState && league.windowState !== windowState) return false
        if (competitionId && league.competitionId !== competitionId) return false
        return true
      }),
    [leagues, searchQ, windowState, competitionId],
  )

  const hasActiveFilter = !!searchQ || !!windowState || !!competitionId

  function clearFilters() {
    void navigate({ search: {} })
    if (inputRef.current) inputRef.current.value = ''
    setSearchVisible(false)
  }

  function toggleSearch() {
    const next = !searchVisible
    setSearchVisible(next)
    if (!next && searchQ) {
      // Close search + clear q, keep other filters
      void navigate({
        search: buildSearch({ windowState, competitionId }),
      })
      if (inputRef.current) inputRef.current.value = ''
    }
    if (next) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  function setWindowFilter(value: 'open' | 'closed' | undefined) {
    void navigate({
      search: buildSearch({ q: searchQ || undefined, windowState: value, competitionId }),
    })
  }

  function setCompetitionFilter(id: string | undefined) {
    void navigate({
      search: buildSearch({ q: searchQ || undefined, windowState, competitionId: id }),
    })
  }

  if (isError) {
    return (
      <>
        <AppHeader title="Suas Ligas" />
        <div className={styles.error}>
          <p className={styles.errorText}>Não foi possível carregar suas ligas.</p>
          <button className={styles.retryBtn} onClick={() => void refetch()}>
            Tentar novamente
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <AppHeader title="Suas Ligas" />

      <div className={styles.page}>
        {/* ── Filter bar ─────────────────────────────────────────── */}
        <div className={styles.filterBar}>
          {/* Search toggle (mobile: icon button; desktop: always shown) */}
          <button
            type="button"
            className={clsx(styles.searchToggle, searchVisible && styles.searchToggleHidden)}
            onClick={toggleSearch}
            aria-label="Buscar ligas"
          >
            <Search size={18} aria-hidden="true" />
          </button>

          <div className={clsx(styles.searchInputWrap, searchVisible && styles.searchExpanded)}>
            <Search size={15} className={styles.searchIcon} aria-hidden="true" />
            <input
              ref={inputRef}
              className={styles.searchInput}
              type="search"
              placeholder="Buscar liga..."
              defaultValue={searchQ}
              onChange={(e) => handleSearch(e.target.value)}
              aria-label="Buscar liga"
            />
            <button
              type="button"
              className={styles.searchClose}
              onClick={toggleSearch}
              aria-label="Fechar busca"
            >
              <X size={15} aria-hidden="true" />
            </button>
          </div>

          {/* Horizontal scrollable chips */}
          <div className={styles.chips} role="group" aria-label="Filtros">
            <button
              type="button"
              className={clsx(styles.chip, !windowState && styles.chipActive)}
              onClick={() => setWindowFilter(undefined)}
            >
              Todas
            </button>
            <button
              type="button"
              className={clsx(styles.chip, windowState === 'open' && styles.chipActive)}
              onClick={() => setWindowFilter('open')}
            >
              Janela aberta
            </button>
            <button
              type="button"
              className={clsx(styles.chip, windowState === 'closed' && styles.chipActive)}
              onClick={() => setWindowFilter('closed')}
            >
              Janela fechada
            </button>

            {competitions.length > 1 &&
              competitions.map((comp) => (
                <button
                  key={comp.id}
                  type="button"
                  className={clsx(
                    styles.chip,
                    competitionId === comp.id && styles.chipActive,
                  )}
                  onClick={() =>
                    setCompetitionFilter(competitionId === comp.id ? undefined : comp.id)
                  }
                >
                  {comp.name}
                </button>
              ))}
          </div>
        </div>

        {/* ── League list ─────────────────────────────────────────── */}
        {filtered.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>Nenhuma liga encontrada.</p>
            {hasActiveFilter && (
              <button type="button" className={styles.clearBtn} onClick={clearFilters}>
                Limpar filtros
              </button>
            )}
          </div>
        ) : (
          <div className={styles.list}>
            {filtered.map((league) => (
              <LeagueSummaryCard
                key={league.id}
                league={league}
                now={now}
                isFavorite={league.isFavorite}
                onToggleFavorite={() => toggleFavorite.mutate({ leagueId: league.id, currentlyFavorite: league.isFavorite })}
                isExpanded={expandedLeagueIds.has(league.id)}
                onTogglePreview={() => setExpandedLeagueIds((prev) => {
                  const next = new Set(prev)
                  if (next.has(league.id)) next.delete(league.id)
                  else next.add(league.id)
                  return next
                })}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}
